<template>
  <section class="work" aria-label="Measurement workspace">
    <VerificationViewer />

    <!-- A single continuous guide owns design, as-built capture, manual entry,
         and review. It borrows the Master Import shell but remains non-modal so
         model and cloud picking can still reach the viewer. -->
    <VerificationWizard
      v-model="wizardOpen"
      :active-plane="activePlane"
      :recapture-plane="recapturePlane"
      :viewer-ready="viewerReady"
      :pick-armed="pickArmed"
      :can-use-point="canUsePoint"
      :conflict="conflict"
      :pending-coarse-pick="pendingCoarsePick"
      :coarse-pick-scale-text="coarsePickScaleText"
      :coarse-pick-needed-text="coarsePickNeededText"
      :show-coarse-warning="showCoarseWarning"
      :coarse-warning="coarseWarning"
      @toggle-arm="onToggleArm"
      @stop-capture="stopWizardCapture"
      @use-point="onUsePoint"
      @manual-confirm="onManualConfirm"
      @manual-mode="onWizardManualMode"
      @discard-coarse="onDiscardCoarsePick"
      @measure-anyway="onMeasureAnyway"
      @resolve-conflict="onResolveConflict"
      @remeasure="startAsBuiltCapture"
      @recapture-plane="startPlaneRecapture"
      @restart-capture="restartAsBuiltDraft"
      @view-change="onViewChange"
    />

    <FloatingDocumentViewer
      :value="!!referenceDocument"
      :document="referenceDocument"
      @close="$emit('close-reference-document')"
    />

    <!-- Renderless. The slice controller owns the slab + the camera framing; it
         emits, we arm. `ref` so the step card's "Pick as-built point" can start
         a capture from the 3D view, where arming is otherwise impossible. Its
         two surfaces worth showing — live screen scale and the refusal notices
         — are relayed below to the status bar, the viewer's only bottom dock. -->
    <SliceController
      ref="sliceController"
      :active-plane="activePlane"
      @plane-change="onPlaneChange"
      @scale-change="onScaleChange"
      @notice-change="onNoticeChange"
    />

    <!-- Status bar owns every viewer toolbar surface: State + Hint, View
         combo, Layers, Scan, Scale, Camera, Style, Fit. No canvas-overlay
         chrome. -->
    <ViewerStatusBar
      ref="viewerStatusBar"
      :scale-state="scaleState"
      :scale-mm-per-px="scaleMmPerPx"
      :scale-tolerance-mm="scaleToleranceMm"
      :slice-notice="sliceNotice"
      @view-change="onViewChange"
    />

    <!-- Step 4 · Dual-offset visual confirmation. Self-gating: renders only
         while the active point carries an offset on BOTH sides, and removes
         itself when that stops holding. Read-only by ticket requirement —
         no confirm, no dismiss, no warning copy.
         It prints POSITIONS, so it needs the unit transform to reach site
         metres; threaded as a prop on the same route ResultsPanel uses for
         PointResultCard. -->
    <OffsetCheckView :unit-transform="unitTransform" />

  </section>
</template>

<script>
/**
 * Centre viewer panel — and the owner of the as-built CAPTURE FLOW (AVA-475
 * Task 5, ticket Step 3).
 *
 * WHAT THIS COMPONENT OWNS
 *   - mounting the continuous VerificationWizard and the SliceController
 *   - relaying the slice controller's screen scale and refusal notices to the
 *     status bar, which is the viewer's single bottom dock
 *   - the cloud-pick ARMING LIFECYCLE: when a pick may be armed, when it must
 *     be disarmed, and cleaning it up on teardown
 *   - folding each pick into the store's combine-views draft, and committing it
 *
 * WHY ARMING IS GATED RATHER THAN ALWAYS-ON
 * The live spike measured pick accuracy as a function of zoom under the same
 * +/-3 px hand wobble: 13.9 mm/px at building view gave 145 mm of scatter,
 * 0.4 mm/px framed to 300 mm gave 4 mm. Tolerances here are +/-3 mm to
 * +/-60 mm, so an un-framed pick reports confident nonsense. SliceController cuts
 * the slab AND frames the camera, then emits `plane-change`; only then can a pick be
 * armed. `canArm` is the single guard every arming path goes through — the
 * emit fires again whenever the design point changes, so a call-site-only check
 * would silently re-arm behind an open modal.
 *
 * COMBINE VIEWS. An orthographic slice pick honestly resolves only the two axes
 * that view can see (XY -> x,y · XZ -> x,z · YZ -> y,z); the third is whatever
 * depth the point happened to sit at inside the slab. So a pick writes two axes
 * into the draft and leaves the third alone, and "Use this point" unlocks only
 * once all three are filled — from one, two or three different views.
 *
 * UNITS. Picked positions are VIEWER units — metres on HPC/EDF, MILLIMETRES on
 * Merchant — and are stored unconverted, which is the whole unit contract
 * (utils/modelUnits.js). Nothing here converts: `computeDeltas` reaches
 * millimetres with the resolved `mmPerViewerUnit`, and the panel's one site-metre
 * surface, OffsetCheckView, is handed the transform as a prop from here.
 * `mmPerPx` is a SCREEN SCALE in millimetres per pixel, not a position.
 *
 * TEARDOWN. Two known ways this feature has hurt before: a viewer tool left
 * armed after unmount, and the crop extension left holding a thin slab as its
 * "current" box (so a later Crop toggle draws handles around a 200 mm slice).
 * `beforeDestroy` therefore disarms the pick, drops the EventBus listener and
 * restores the SUPPORT crop box — never a bare `clearSlice`, which would strip
 * the isolation crop entirely.
 */
import { mapGetters } from 'vuex';

import FloatingDocumentViewer from '@/features/Verification/components/workspace/FloatingDocumentViewer.vue';
import OffsetCheckView from '@/features/Verification/components/workspace/OffsetCheckView.vue';
import SliceController from '@/features/Verification/components/workspace/SliceController.vue';
import VerificationWizard from '@/features/Verification/components/workspace/VerificationWizard.vue';
import ViewerStatusBar from '@/features/Verification/components/workspace/ViewerStatusBar.vue';
import VerificationViewer from '@/features/Verification/components/workspace/VerificationViewer.vue';
import {
  PLANE_3D,
  capturePlaneForView,
  isSlicePlane,
  viewForCapturePlane,
} from '@/features/Verification/utils/asBuiltDraft';
import { enableCloudPick, disableCloudPick } from '@/features/Verification/utils/cloudPick';
import { clearSlice } from '@/features/Verification/utils/sliceController';
import { CAPTURE_EVENTS } from '@/features/Verification/utils/captureEvents';

import { EventBus } from '@/event-bus';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * Declared in VerificationViewer.vue and DesignPointModal.vue too. The model
 * pick and the cloud pick both bind `click` on the same canvas, and neither
 * util disarms the other — so one click could write two different points from
 * one gesture. We cancel the model pick before arming, and stand down when the
 * design modal arms one. Keep the three copies in step.
 */
const MODEL_PICK_EVENTS = Object.freeze({
  ARM: 'verification:arm-model-pick',
  CANCEL: 'verification:cancel-model-pick',
  RESULT: 'verification:model-pick',
  FAILED: 'verification:model-pick-failed',
});

/** The `3D` tab is not a slice plane — it restores the support crop. */
/** `scale-change` states emitted by SliceController. Only COARSE is a warning. */
const SCALE_STATE = Object.freeze({
  UNKNOWN: 'unknown',
  NO_TOLERANCE: 'no-tolerance',
  ADEQUATE: 'adequate',
  COARSE: 'coarse',
});

export default {
  name: 'WorkspacePanel',
  components: {
    FloatingDocumentViewer,
    OffsetCheckView,
    SliceController,
    VerificationViewer,
    VerificationWizard,
    ViewerStatusBar,
  },
  props: {
    referenceDocument: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      /** Does the SURVEYOR want a pick armed? Separate from whether it CAN be. */
      armIntent: false,
      /** Plane being explicitly replaced. Null during ordinary first capture. */
      recapturePlane: null,
      pickArmed: false,
      wizardOpen: false,
      wizardManualMode: false,
      /** Worst-axis screen scale at the last camera settle, mm per pixel. */
      scaleMmPerPx: null,
      scaleToleranceMm: null,
      scaleState: SCALE_STATE.UNKNOWN,
      // Why the slice controller last refused to cut. Relayed to the status
      // bar's hint slot: an empty viewport with no explanation is how a frame
      // mismatch gets misread as "the slice feature is broken".
      sliceNotice: '',
      // A pick the scale gate is holding. Component-local on purpose: it is
      // pre-merge and inseparable from the camera framing that produced it, so
      // it SHOULD die on unmount rather than outlive the view it describes.
      pendingCoarsePick: null,
    };
  },

  computed: {
    ...mapGetters('VERIFICATION', {
      activePoint: 'GET_ACTIVE_POINT',
      pointsWithResults: 'GET_POINTS_WITH_RESULTS',
      draftStatus: 'GET_AS_BUILT_DRAFT_STATUS',
      cropBox: 'GET_VERIFIED_CROP_BOX',
      viewerStatus: 'GET_VIEWER_STATUS',
      activePlane: 'GET_ACTIVE_CAPTURE_PLANE',
      unitTransform: 'GET_UNIT_TRANSFORM',
    }),

    /** The Forge viewer lives in ROOT state; never reached from the template. */
    viewer() {
      return this.$store.state.viewer;
    },

    viewerReady() {
      return this.viewerStatus === 'ready' || this.viewerStatus === 'isolating';
    },

    activePointId() {
      return this.activePoint?.id || null;
    },

    /**
     * THE arming guard. Every arm path checks this rather than its own call
     * site: SliceController re-emits `plane-change` whenever the design point
     * moves, so a call-site-only check would silently re-arm behind an open
     * modal. `wizardManualMode` is read HERE for that reason — the controller
     * is renderless and has no gate of its own to consult.
     */
    canArm() {
      return this.viewerReady
        && isSlicePlane(this.activePlane)
        && !this.wizardManualMode
        && !!this.activePointId;
    },

    canToggleArm() {
      return this.canArm || this.pickArmed;
    },

    /** The draft only counts when it belongs to the point on screen. */
    draftForActivePoint() {
      const draft = this.draftStatus;
      if (!draft || !this.activePointId) return null;
      return draft.pointId === this.activePointId ? draft : null;
    },

    /** The held pick, when the agreement gate is waiting on the active point. */
    conflict() {
      return this.draftForActivePoint?.pendingConflict || null;
    },

    /**
     * Measured scale of the held pick, e.g. "13.9 mm/px". One decimal always —
     * it sits directly beside the "need <= 1.0 mm/px" target, and rounding 13.9
     * to "14" next to "1.0" reads as sloppy arithmetic rather than a measurement.
     */
    coarsePickScaleText() {
      const mm = this.pendingCoarsePick?.mmPerPx;
      return Number.isFinite(mm) ? `${mm.toFixed(1)} mm/px` : 'scale unknown';
    },

    /**
     * The scale this view would need. `tolerance / 3` is the same threshold the
     * gate fires on, restated as a target so "zoom in" has a finish line rather
     * than being an instruction to guess.
     */
    coarsePickNeededText() {
      const tol = this.pendingCoarsePick?.toleranceMm;
      if (!Number.isFinite(tol)) return '—';
      return `${(tol / 3).toFixed(1)} mm/px`;
    },

    /**
     * `isCommittable`, NOT `isComplete`. They differ exactly when the gate holds
     * a disputed pick: all three axes read as filled, but one of them is
     * contested. `draftToPoint` and `commitAsBuiltDraft` both enforce
     * `isCommittable`, so gating the button on `isComplete` would offer an
     * action that silently does nothing.
     */
    canUsePoint() {
      return !!this.draftForActivePoint?.isCommittable && !this.wizardManualMode;
    },

    /**
     * Repeated from the status bar's Scale cell on purpose. That cell explains
     * the VIEW; this one sits beside the button that writes the claim onto the
     * record.
     */
    showCoarseWarning() {
      return this.scaleState === SCALE_STATE.COARSE
        && (this.pickArmed || this.canUsePoint || !!this.draftForActivePoint?.planes?.length);
    },

    coarseWarning() {
      const scale = this.scaleMmPerPx === null ? '—' : `${this.scaleMmPerPx.toFixed(2)} mm/px`;
      const tolerance = this.scaleToleranceMm === null ? '—' : `±${this.scaleToleranceMm} mm`;
      return `View is ${scale} — too coarse to assess ${tolerance}`;
    },
  },

  watch: {
    /**
     * Single reconciler. Covers every way arming can become illegal without a
     * plane change: viewer reload, task switch, point deleted, modal opened.
     */
    canArm() {
      this.syncPick('canArm changed');
    },

    /**
     * A held coarse pick describes ONE point in ONE framing. The moment either
     * changes it is stale, and offering "Measure anyway" on it would write a
     * reading taken against a different point or a different camera. Dropped
     * silently — there is nothing for the surveyor to adjudicate once the thing
     * being measured has moved.
     */
    activePointId(pointId, previousPointId) {
      this.recapturePlane = null;
      if (this.pendingCoarsePick) {
        debugLog('[WorkspacePanel] dropping held coarse pick — active point changed');
        this.pendingCoarsePick = null;
      }
      if (pointId && pointId !== previousPointId) this.wizardOpen = true;
    },

    activePlane() {
      if (this.pendingCoarsePick) {
        debugLog('[WorkspacePanel] dropping held coarse pick — slice plane changed');
        this.pendingCoarsePick = null;
      }
    },

    /**
     * Remember the last real support crop. `GET_VERIFIED_CROP_BOX` goes null
     * whenever `RESOLVE_VERIFIED_GEOMETRY` fails to probe the geometry
     * (verification.module.js:853-860 commits null on a transient miss), which
     * is reachable mid-route on a task switch. Teardown must never hand
     * `clearSlice` a null in that window — that strips the isolation crop
     * entirely instead of restoring it.
     */
    cropBox: {
      immediate: true,
      handler(box) {
        if (box) this._lastCropBox = box;
      },
    },
  },

  created() {
    // Non-reactive: it holds a live THREE.Box3 and only ever feeds teardown.
    this._lastCropBox = null;
    this._cursorTarget = null;
    this._cursorPrev = null;

    this._onModelPickArmed = () => {
      if (!this.pickArmed && !this.armIntent) return;
      debugLog('[WorkspacePanel] model pick armed elsewhere — standing down');
      this.armIntent = false;
      this.syncPick('model pick took the canvas');
    };
    EventBus.$on(MODEL_PICK_EVENTS.ARM, this._onModelPickArmed);

    this._onStartAsBuilt = () => this.startAsBuiltCapture();
    EventBus.$on(CAPTURE_EVENTS.START_AS_BUILT, this._onStartAsBuilt);

    this._onOpenWizard = () => {
      if (this.activePointId) this.wizardOpen = true;
    };
    EventBus.$on(CAPTURE_EVENTS.OPEN_WIZARD, this._onOpenWizard);
  },

  beforeDestroy() {
    EventBus.$off(MODEL_PICK_EVENTS.ARM, this._onModelPickArmed);
    this._onModelPickArmed = null;
    EventBus.$off(CAPTURE_EVENTS.START_AS_BUILT, this._onStartAsBuilt);
    this._onStartAsBuilt = null;
    EventBus.$off(CAPTURE_EVENTS.OPEN_WIZARD, this._onOpenWizard);
    this._onOpenWizard = null;

    // 1. Never leave a click handler on a canvas this route no longer owns.
    this.armIntent = false;
    this.disarmPick('component destroyed');

    // 2. Snapshot the live measurement box before teardown restores the
    //    support crop. Drag-end normally does this immediately; this fallback
    //    also protects route changes and hot reload at the lifecycle boundary.
    this.$refs.sliceController?.persistMeasurementCrop?.();

    // 3. Hand the crop extension the SUPPORT box back. A bare clearSlice would
    //    strip the isolation crop; leaving the slab in place would make a later
    //    Crop toggle draw handles around a 200 mm slice.
    this.restoreSupportCrop('component destroyed');
  },

  methods: {
    // ---- SliceController events -------------------------------------------

    /**
     * Fired only after a slab was actually cut and the camera framed (or the
     * camera changed to `3D`), so this is the earliest honest moment to
     * arm. Also re-fires when the design point moves and the slice is re-cut.
     */
    /**
     * "Pick as-built point" from the step card.
     *
     * The pick cannot arm from the `3D` tab — an unframed pick scatters ~145 mm
     * against a ±3 mm tolerance, so the slice-and-frame is a precondition, not a
     * preference. But refusing silently is what made the step card's button look
     * broken: it said "pick on the cloud" while the only thing standing in the
     * way was named in a 34 px bar the surveyor is not looking at.
     *
     * The guide now presents XY, XZ, and YZ equally. Once the surveyor chooses,
     * SliceController performs the slice-and-frame and the normal plane-change
     * path becomes the single route into the armed state.
     */
    startAsBuiltCapture() {
      if (!this.activePointId) {
        debugWarn('[WorkspacePanel] as-built capture requested with no active point');
        return;
      }
      if (isSlicePlane(this.activePlane)) {
        if (this.canUsePoint) {
          debugLog('[WorkspacePanel] completed draft needs an explicit recapture plane');
          return;
        }
        this.armIntent = true;
        this.syncPick('step card requested capture');
        return;
      }
      this.wizardOpen = true;
      this.armIntent = false;
      this.syncPick('waiting for surveyor to choose a capture view');
      debugLog('[WorkspacePanel] awaiting explicit first capture view');
    },

    onPlaneChange(payload) {
      const event = typeof payload === 'string'
        ? { plane: payload, captureIntent: true, source: 'legacy' }
        : (payload || {});
      const plane = event.plane || PLANE_3D;
      debugLog('[WorkspacePanel] plane changed', {
        plane,
        viewId: event.viewId,
        source: event.source,
        captureIntent: event.captureIntent,
        navigationOnly: event.navigationOnly,
        was: this.activePlane,
      });

      this.$store.commit('VERIFICATION/SET_ACTIVE_CAPTURE_PLANE', plane);
      // Each plane is captured by an OPPOSITE PAIR of views, so the plane alone
      // does not identify one. Prefer the view the payload carries and fall back
      // to the pair's canonical member only for the legacy string payload above,
      // which has no view to carry. Resolved BEFORE both uses below: the commit
      // fixes the combo's label, but the `applyView` on the navigation-only
      // branch is what actually points the camera — a canonical-twin pose there
      // mirrors the projection every following pick is read against.
      const viewId = event.viewId || viewForCapturePlane(plane);
      if (viewId) this.$store.commit('VERIFICATION/SET_ACTIVE_VIEW', viewId);

      if (plane === PLANE_3D) {
        this.recapturePlane = null;
        this.armIntent = false;
        // SliceController has restored the support crop; now apply the Iso pose.
        this.$refs.viewerStatusBar?.applyView?.('iso', { commit: false });
      } else if (event.navigationOnly) {
        // No Design coordinate exists yet, so SliceController cannot cut a slab.
        // Navigation remains fully synchronized and capture remains disarmed.
        this.armIntent = false;
        this.$refs.viewerStatusBar?.applyView?.(viewId, { commit: false });
      } else {
        const draftNeedsAxes = !this.draftForActivePoint?.isCommittable;
        this.armIntent = !!event.captureIntent
          && (draftNeedsAxes || this.recapturePlane === plane);
      }
      this.syncPick(`plane ${plane} from ${event.source || 'unknown'}`);
    },

    /**
     * View and Slice are two presentations of one canonical navigation state.
     * A named orthographic view applies its corresponding slice, then follows
     * the same capture-intent gate as a direct Slice click.
     *
     * TWO SENDERS, ONE HANDLER: the bottom-bar View menu and the wizard's
     * quick-view buttons both emit `view-change` to here. Neither holds view
     * logic of its own — a sender that posed the camera itself would look
     * identical and skip the slice this cuts.
     */
    onViewChange(viewId) {
      const plane = capturePlaneForView(viewId);
      if (!plane) return;
      const applied = this.$refs.sliceController?.selectPlane?.(plane, {
        captureIntent: plane !== PLANE_3D,
        source: 'view',
        // WHICH member of the plane's opposite pair the surveyor chose. Without
        // it the slice would be cut correctly and then viewed from the far side.
        viewId,
      });
      if (!applied) {
        // Before a design point exists there is nowhere honest to cut a slab.
        // Keep plain camera navigation available without claiming a slice.
        this.$refs.viewerStatusBar?.applyView?.(viewId);
      }
    },

    startPlaneRecapture(plane) {
      const contributions = this.$store.getters['VERIFICATION/GET_AS_BUILT_DRAFT']?.contributions;
      if (!isSlicePlane(plane) || !contributions?.[plane]) {
        debugWarn('[WorkspacePanel] recapture ignored — no contribution for plane', plane);
        return;
      }
      this.wizardOpen = true;
      this.recapturePlane = plane;
      const applied = this.$refs.sliceController?.selectPlane?.(plane, {
        captureIntent: true,
        source: 'recapture',
      });
      if (!applied) this.recapturePlane = null;
    },

    restartAsBuiltDraft() {
      if (!this.activePointId) return;
      this.recapturePlane = null;
      this.armIntent = false;
      this.syncPick('capture restarted');
      this.$store.dispatch('VERIFICATION/clearAsBuiltDraft', this.activePointId);
    },

    /** Pure relay to the status bar. `''` clears the hint back to the default. */
    onNoticeChange(notice) {
      this.sliceNotice = notice || '';
    },

    onScaleChange(payload) {
      this.scaleMmPerPx = payload?.mmPerPx ?? null;
      this.scaleToleranceMm = payload?.toleranceMm ?? null;
      this.scaleState = payload?.state || SCALE_STATE.UNKNOWN;
      debugLog('[WorkspacePanel] screen scale', {
        mmPerPx: this.scaleMmPerPx,
        toleranceMm: this.scaleToleranceMm,
        state: this.scaleState,
      });
    },

    // ---- Arming lifecycle -------------------------------------------------

    onToggleArm() {
      this.armIntent = this.pickArmed ? false : !this.canUsePoint;
      if (!this.armIntent) this.recapturePlane = null;
      debugLog('[WorkspacePanel] arm toggled by user', { armIntent: this.armIntent });
      this.syncPick('user toggle');
    },

    /**
     * Reconcile the real handler with `armIntent && canArm`. Idempotent, so it
     * is safe to call from a watcher and a handler for the same change.
     */
    syncPick(reason) {
      const should = this.armIntent && this.canArm;
      if (should && !this.pickArmed) this.armPick(reason);
      else if (!should && this.pickArmed) this.disarmPick(reason);
    },

    armPick(reason) {
      const viewer = this.viewer;
      if (!viewer) {
        debugWarn('[WorkspacePanel] cannot arm — no viewer', reason);
        return;
      }
      // The model pick binds the same canvas click and neither util knows about
      // the other; without this, one click writes two different points.
      EventBus.$emit(MODEL_PICK_EVENTS.CANCEL);

      // The disposer is deliberately not stashed: enableCloudPick is a module
      // singleton that disarms ITSELF before invoking onPick, so a stored
      // handle goes stale on the first pick. disableCloudPick() is the only
      // teardown that cannot desync.
      enableCloudPick(viewer, this.onCloudPick);
      this.pickArmed = true;
      this.setCanvasCursor('crosshair');
      debugLog('[WorkspacePanel] cloud pick armed', {
        reason,
        plane: this.activePlane,
        pointId: this.activePointId,
      });
    },

    disarmPick(reason) {
      disableCloudPick();
      this.setCanvasCursor(null);
      if (this.pickArmed) debugLog('[WorkspacePanel] cloud pick disarmed', { reason });
      this.pickArmed = false;
    },

    /** Restores the previous cursor when `cursor` is null. Always symmetric. */
    setCanvasCursor(cursor) {
      if (cursor) {
        const target = this.viewer?.canvas || this.viewer?.container || null;
        if (!target) return;
        if (!this._cursorTarget) {
          this._cursorTarget = target;
          this._cursorPrev = target.style.cursor;
        }
        target.style.cursor = cursor;
        return;
      }
      if (this._cursorTarget) {
        this._cursorTarget.style.cursor = this._cursorPrev || '';
      }
      this._cursorTarget = null;
      this._cursorPrev = null;
    },

    // ---- Capture ----------------------------------------------------------

    /**
     * One pick, two axes. The third axis of `point` is the depth the pick
     * landed at inside the slab and is discarded by `mergeAsBuiltDraftAxes`.
     */
    onCloudPick(point) {
      // enableCloudPick is one-shot and has already detached itself.
      this.pickArmed = false;
      this.setCanvasCursor(null);

      const plane = this.activePlane;
      const pointId = this.activePointId;
      if (!pointId || !isSlicePlane(plane)) {
        debugWarn('[WorkspacePanel] pick dropped — no point or not a slice plane', {
          pointId, plane,
        });
        return;
      }

      debugLog('[WorkspacePanel] cloud pick received (metres)', {
        pointId, plane, point, mmPerPx: this.scaleMmPerPx,
      });

      // SCALE GATE. At 13.9 mm/px a +/-3px hand wobble scatters the pick 145 mm;
      // framed to 300 mm it scatters 4 mm. Against a +/-3 mm tolerance the coarse
      // reading is ~50x out, which is not a low-confidence measurement — it is
      // not a measurement. Warning about it at save time is too late to act on:
      // the surveyor has already switched views twice, and a plausible-looking
      // number plus sunk cost is exactly when warnings get dismissed. So the
      // pick is HELD at the moment it is taken, when re-framing costs one click.
      //
      // But never a dead end. A hard block with no exit gets routed around by
      // typing the number into the manual dialog, which launders an invalid
      // measurement into a "manual" entry carrying NO scale record at all —
      // strictly worse than an honest override. "Measure anyway" proceeds and
      // stamps the real mmPerPx onto the reading, from which PointResultCard and
      // the report derive LOW CONFIDENCE. No extra field: the number is the record.
      if (this.scaleState === SCALE_STATE.COARSE) {
        this.pendingCoarsePick = {
          pointId, plane, point, mmPerPx: this.scaleMmPerPx, toleranceMm: this.scaleToleranceMm,
        };
        debugWarn('[WorkspacePanel] pick HELD — screen scale too coarse to measure', {
          mmPerPx: this.scaleMmPerPx, toleranceMm: this.scaleToleranceMm,
        });
        this.armIntent = false;
        this.syncPick('pick held — view too coarse');
        return;
      }

      this.mergePick({ pointId, plane, point, mmPerPx: this.scaleMmPerPx });
    },

    /**
     * Fold a pick into the draft. Split out of `onCloudPick` so the scale gate's
     * "Measure anyway" reaches the identical path — an override must produce the
     * same record as a normal pick, differing only in the mmPerPx it carries.
     */
    mergePick({ pointId, plane, point, mmPerPx }) {
      // Scan provenance defaults from the store at pick time. `mmPerPx` is the
      // last SETTLED measurement (SliceController debounces the camera), and it is
      // unrecoverable later, so it is always passed.
      this.$store
        .dispatch('VERIFICATION/mergeAsBuiltDraftAxes', {
          pointId,
          plane,
          point,
          mmPerPx,
        })
        .then((status) => {
          this.recapturePlane = null;
          debugLog('[WorkspacePanel] draft updated', {
            filled: status?.filledAxes,
            missing: status?.missingAxes,
            isComplete: status?.isComplete,
            hasConflict: status?.hasConflict,
          });
          if (status?.hasConflict) {
            // The agreement gate is holding this pick. Stand down: every further
            // pick is rejected until it is adjudicated, so staying armed would
            // let the surveyor click the cloud repeatedly with nothing on screen
            // changing — the exact dead-end this gate exists to make visible.
            this.armIntent = false;
            this.syncPick('pick held — views disagree');
            return;
          }
          if (status?.isCommittable) {
            // All three axes are in and none disputed. Stand down rather than
            // staying armed over a finished draft — a stray click would
            // overwrite two axes with no visible change to the chips, which are
            // already lit.
            this.armIntent = false;
            this.syncPick('draft complete');
            return;
          }
          // A slice pick is one deliberate wizard action. Stop after it and let
          // the guide recommend the next view, rather than silently re-arming
          // the canvas and turning the next click into an accidental overwrite.
          this.armIntent = false;
          this.syncPick('slice pick captured — await wizard continuation');
        })
        .catch((err) => {
          debugError('[WorkspacePanel] merging the pick failed', err);
        });
    },

    /** Discard a pick the scale gate held. The surveyor zooms in and re-picks. */
    onDiscardCoarsePick() {
      if (!this.pendingCoarsePick) return;
      debugLog('[WorkspacePanel] coarse pick discarded — surveyor will re-frame', {
        mmPerPx: this.pendingCoarsePick.mmPerPx,
      });
      this.pendingCoarsePick = null;
      this.armIntent = true;
      this.syncPick('re-arm after discarding a coarse pick');
    },

    /**
     * Take the held pick anyway. Legitimate cases exist — a sparse cloud, a
     * distant feature, a +/-60 mm tolerance where 5 mm/px is fine. The override
     * is honest: it records the real mmPerPx, which is what makes the reading
     * self-evidently low confidence wherever it is later read.
     */
    onMeasureAnyway() {
      const held = this.pendingCoarsePick;
      if (!held) return;
      debugWarn('[WorkspacePanel] coarse pick ACCEPTED on override — recorded as low confidence', {
        mmPerPx: held.mmPerPx, toleranceMm: held.toleranceMm,
      });
      this.pendingCoarsePick = null;
      this.mergePick(held);
    },

    /**
     * Adjudicate a pick the agreement gate is holding.
     * `replace` accepts the new pick in full — including its other axis, which
     * is equally suspect if the shared one disagreed. `keep` discards it whole.
     */
    onResolveConflict(choice) {
      const conflict = this.conflict;
      if (!conflict) return;
      debugLog('[WorkspacePanel] resolving axis disagreement', {
        choice,
        plane: conflict.plane,
        worstDeltaMm: conflict.worstDeltaMm,
        axes: conflict.disagreements.map((d) => d.axis),
      });
      this.$store.commit('VERIFICATION/RESOLVE_AS_BUILT_CONFLICT', choice);
    },

    onUsePoint() {
      const draft = this.draftForActivePoint;
      if (!draft?.isCommittable) {
        debugWarn('[WorkspacePanel] use-point blocked', {
          missing: draft?.missingAxes,
          hasConflict: draft?.hasConflict,
        });
        return;
      }
      // Read the offset from the point the DRAFT belongs to, not the live
      // active point: setAsBuiltPoint is a full replace, so a wrong-point
      // offset would silently rewrite the wrong record.
      const target = this.pointsWithResults.find((p) => p.id === draft.pointId) || null;
      if (!target) {
        debugWarn('[WorkspacePanel] use-point blocked — point gone', draft.pointId);
        return;
      }

      debugLog('[WorkspacePanel] committing as-built draft', {
        pointId: draft.pointId,
        planes: draft.planes,
        scanNames: draft.scanNames,
        worstMmPerPx: draft.worstMmPerPx,
      });
      this.$store
        .dispatch('VERIFICATION/commitAsBuiltDraft', {
          id: draft.pointId,
          offset: target.asBuiltOffset ?? null,
        })
        .then((ok) => {
          if (!ok) {
            debugWarn('[WorkspacePanel] commit refused — draft kept', draft.pointId);
            return;
          }
          debugLog('[WorkspacePanel] as-built committed', draft.pointId);
          // The reading is written; stop listening for clicks so a stray one
          // cannot start a new draft the surveyor did not ask for.
          this.armIntent = false;
          this.recapturePlane = null;
          this.syncPick('as-built committed');
        })
        .catch((err) => {
          debugError('[WorkspacePanel] commitAsBuiltDraft failed', err);
        });
    },

    stopWizardCapture() {
      this.armIntent = false;
      this.recapturePlane = null;
      this.syncPick('wizard stopped capture');
    },

    onWizardManualMode(isManual) {
      this.wizardManualMode = !!isManual;
      if (!isManual) return;
      this.armIntent = false;
      this.recapturePlane = null;
      this.syncPick('manual entry opened inside wizard');
    },

    // ---- Manual fallback --------------------------------------------------

    /**
     * The payload is a verbatim `setAsBuiltPoint` argument, including the
     * provenance the modal narrowed to the axes still backed by a pick.
     */
    onManualConfirm(payload) {
      debugLog('[WorkspacePanel] manual as-built confirmed', payload);
      this.$store.dispatch('VERIFICATION/setAsBuiltPoint', payload);
      // The modal does not clear the draft — the reading it produced supersedes
      // it, so leaving it would keep the axis chips lit against nothing.
      this.$store.dispatch('VERIFICATION/clearAsBuiltDraft', payload.id);
    },

    // ---- Teardown ---------------------------------------------------------

    restoreSupportCrop(reason) {
      const viewer = this.viewer;
      if (!viewer) return;
      // Falls back to the cached box: a stale support crop is recoverable, a
      // null one silently un-isolates the whole model.
      const restoreBox = this.cropBox || this._lastCropBox || null;
      try {
        clearSlice(viewer, restoreBox);
        debugLog('[WorkspacePanel] slice cleared, support crop restored', {
          reason,
          restored: !!restoreBox,
          fromCache: !this.cropBox && !!restoreBox,
        });
      } catch (err) {
        debugError('[WorkspacePanel] restoring the support crop failed', err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.work {
  background: $vf-work-bg;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
</style>
